<script lang="ts">
import VueMultiselect from 'vue-multiselect';
import { onMounted, ref } from 'vue';
import type { Languages } from '../../types/window';
import Icon from '../Icon/Icon.vue';

type Props = {
  options: Languages[];
  selectedLanguage: string;
};
</script>
<script setup lang="ts">
const props = defineProps<Props>();

const switcher = ref<VueMultiselect | null>(null);
const value = ref<Languages | null>(
  props.options.find((option) => option.language === props.selectedLanguage) || null,
);
let loading = ref(true);
let event = ref<CustomEvent | null>(null);

const switchLanguage = (option: Languages) => {
  if (loading.value || !event.value) {
    return;
  }

  window.dispatchEvent(event.value);
  window.location.href = option.url;
};

onMounted(() => {
  event.value = new CustomEvent('externalFilterReset');
  loading.value = false;
  const initialOption = props.options.find((option) => option.language === props.selectedLanguage);

  if (!value.value && initialOption) {
    value.value = initialOption;
    switchLanguage(initialOption);
  }
});
</script>
<template>
  <div v-show="!loading" :class="$style.select">
    <VueMultiselect
      v-if="options.length > 1"
      ref="switcher"
      v-model="value"
      :allow-empty="false"
      aria-label="Language select"
      :hide-selected="false"
      label="language"
      :options="options"
      :preselect-first="true"
      :searchable="false"
      :show-labels="false"
      track-by="language"
      @select="switchLanguage"
    >
      <template #singleLabel="selectProps">
        <Icon name="language" size="large" />
        <span class="option__title">{{ selectProps.option.language }}</span>
      </template>
      <template #option="selectProps">
        <div class="option__desc">
          <span class="option__title">
            {{ selectProps.option.label }}
          </span>
        </div>
      </template>
    </VueMultiselect>
    <div v-else :class="$style.noSelect">
      <Icon name="language" size="large" />
      <span class="option__title">{{ options.at(0)?.language }}</span>
    </div>
  </div>
</template>

<style src="vue-multiselect/dist/vue-multiselect.css"></style>
<style module lang="scss">
@forward './LanguageSwitcher.scss';
</style>
